import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  PencilSquareIcon as PencilOutlineIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { PencilSquareIcon as PencilSolidIcon } from "@heroicons/react/24/solid";

import RowNotesModal from "@components/notes/RowNotesModal";
import { DBTables } from "@utils/constants/dbTables";
import type { MeterUploadRowData } from "@utils/bulkMeterUpload/buildTrialRunRow";

// A pre-ledger row note is free text that becomes a *declaration note* at ledger
// time — it is NOT an Enhanced Note: no persisted Note entity exists yet, so the
// existing NoteIndicatorBadge / RowNotesModal (which need a persisted reference)
// are intentionally not reused. This cell edits rowData.note directly and commits
// through the same inline path as the value cells.

type RowNoteCellProps = {
  rowData: MeterUploadRowData;
  // Non-ledgered rows can add / edit / clear; ledgered rows are view-only — the
  // marker opens the real declaration note in a read-only modal so it can be read
  // but not changed.
  editable: boolean;
  // The declaration the row ledgered into. Present only on ledgered rows; lets the
  // view-only marker open the persisted declaration note via RowNotesModal. Absent
  // on editable rows (and, defensively, falls back to the local text view).
  declarationId?: string | null;
  // Commits the patched rowData through the same path as the value cells
  // (useBulkMeterUploadFlow.editRow keeps the local-vs-persist decision). The row
  // note is deliberately excluded from trialRunSig, so committing it does not
  // re-run the trial.
  onCommit: (rowData: MeterUploadRowData) => void | Promise<void>;
};

// Trim and treat blank as "no note" so an empty note never creates a declaration
// note at ledger time (the server skips empty notes — existing behaviour).
const normalizeNote = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  return trimmed === "" ? undefined : trimmed;
};

const hasRowNote = (rowData: MeterUploadRowData): boolean =>
  !!rowData.note && rowData.note.trim() !== "";

// A single trial-run grid cell that signals + edits the row note. A quiet outline
// pencil invites the first note; a filled pencil marks a row that already carries
// one. Clicking opens a small editor for add / edit / clear.
const RowNoteCell: React.FunctionComponent<RowNoteCellProps> = ({
  rowData,
  editable,
  declarationId,
  onCommit,
}) => {
  const { t } = useTranslation();
  const hasNote = hasRowNote(rowData);
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(rowData.note ?? "");

  const addLabel = t("bulk_meter_upload.note.add");
  const editLabel = t("bulk_meter_upload.note.edit");

  // Ledgered rows are view-only. The row's note is now a persisted declaration
  // note, so — when we know the declaration — surface it through the same read-only
  // Notes view the rest of the app uses (RowNotesModal / NotesPanel, the
  // GlobalNotesTable pattern) scoped to (Declarations, declarationId). readOnly
  // strips every write affordance: the note is read but never edited or posted from
  // the grid (note management lives on the declaration; see ADR 0002). Nothing
  // renders when there is no note (no first-note affordance on a committed row).
  if (!editable) {
    if (!hasNote) return null;
    const viewLabel = t("bulk_meter_upload.note.view");

    if (declarationId) {
      return (
        <>
          <button
            type="button"
            data-row-note
            data-has-note="true"
            aria-label={viewLabel}
            title={viewLabel}
            onClick={() => setOpen(true)}
            className="inline-flex items-center rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />
          </button>

          {open ? (
            <RowNotesModal
              open
              readOnly
              referenceTable={DBTables.Declarations}
              referenceId={declarationId}
              artifactName={rowData.extractionPointName}
              onClose={() => setOpen(false)}
            />
          ) : null}
        </>
      );
    }

    // Defensive fallback: a ledgered row with no declaration reference (shouldn't
    // happen) still shows its committed note text, read-only, so it's never
    // unreachable.
    return (
      <div className="relative">
        <button
          type="button"
          data-row-note
          data-has-note="true"
          aria-label={viewLabel}
          title={viewLabel}
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center rounded px-1.5 py-0.5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <EyeIcon className="h-4 w-4" aria-hidden="true" />
        </button>

        {open ? (
          <div className="absolute right-0 z-10 mt-1 w-64 rounded border border-gray-200 bg-white p-3 shadow-lg">
            <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
              {rowData.note}
            </p>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                className="btn-outline-primary text-xs rounded"
                onClick={() => setOpen(false)}
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const openEditor = () => {
    setValue(rowData.note ?? "");
    setOpen(true);
  };

  const commitNote = (note: string | undefined) => {
    onCommit({ ...rowData, note });
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-row-note
        data-has-note={hasNote ? "true" : "false"}
        aria-label={hasNote ? editLabel : addLabel}
        title={hasNote ? editLabel : addLabel}
        onClick={openEditor}
        className={classNames(
          "inline-flex items-center rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500",
          hasNote
            ? "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            : "text-gray-400 hover:bg-gray-100 hover:text-gray-700",
        )}
      >
        {hasNote ? (
          <PencilSolidIcon className="h-4 w-4" aria-hidden="true" />
        ) : (
          <PencilOutlineIcon className="h-4 w-4" aria-hidden="true" />
        )}
      </button>

      {open ? (
        <div className="absolute right-0 z-10 mt-1 w-64 rounded border border-gray-200 bg-white p-3 shadow-lg">
          <textarea
            autoFocus
            rows={3}
            value={value}
            aria-label={t("bulk_meter_upload.table.note") || undefined}
            placeholder={t("bulk_meter_upload.note.placeholder") || undefined}
            onChange={e => setValue(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-blue-500"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              className="btn-outline-primary text-xs rounded"
              onClick={() => setOpen(false)}
            >
              {t("common.cancel")}
            </button>
            {hasNote ? (
              <button
                type="button"
                className="btn-outline-primary text-xs rounded"
                onClick={() => commitNote(undefined)}
              >
                {t("bulk_meter_upload.note.clear")}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-primary text-xs rounded"
              onClick={() => commitNote(normalizeNote(value))}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RowNoteCell;
